import { useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { CalendarCheck, CheckCircle2, FileCheck, MessageCircle } from "lucide-react";
import { trackMeta } from "../lib/metaPixel";

const LOGO_SRC = "/neXstrength-logo.png";
const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
  { icon: FileCheck, title: "We review your details", text: "Our team personally goes through every application." },
  { icon: MessageCircle, title: "WhatsApp confirmation", text: "You'll get a message from us to schedule your call." },
  { icon: CalendarCheck, title: "Join your personal call", text: "Show up at your chosen time — that's it." },
];

export default function ThankYou() {
  const location = useLocation();
  const fullName = location.state?.name || "";
  const firstName = fullName.split(/\s+/)[0];

  useEffect(() => {
    trackMeta("PageView");
  }, []);

  return (
    <div className="relative min-h-screen bg-white text-[#0A0A0A]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px 480px at 50% -8%, rgba(242,92,5,0.08), transparent 65%)",
        }}
      />

      <main className="mx-auto flex w-full max-w-[560px] flex-col items-center px-5 pb-16 pt-10 text-center sm:pt-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center justify-center gap-3 sm:gap-3.5"
        >
          <img
            src={LOGO_SRC}
            alt="NeXstrength logo"
            data-testid="thankyou-logo"
            className="h-[52px] w-auto object-contain sm:h-[58px]"
          />
          <div className="flex flex-col items-stretch text-left">
            <div className="text-[26px] font-extrabold leading-none tracking-[-0.02em] sm:text-[29px]">
              Ne<span className="text-[#F25C05]">X</span>strength
            </div>
            <div className="mt-[6px] flex w-full justify-between text-[8px] font-semibold leading-none sm:text-[9px]">
              {"BUSINESS CONSULTANCY".split("").map((ch, i) =>
                ch === " " ? (
                  <span key={i} className="w-[5px]" />
                ) : (
                  <span key={i}>{ch}</span>
                )
              )}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 14, stiffness: 220, delay: 0.25 }}
          className="mt-12 flex h-20 w-20 items-center justify-center rounded-full bg-[#F25C05]/10"
        >
          <CheckCircle2 data-testid="thankyou-check" className="h-11 w-11 text-[#F25C05]" strokeWidth={2.2} />
        </motion.div>

        <h1
          data-testid="thankyou-headline"
          className="mt-7 text-[clamp(2.2rem,9vw,3.2rem)] font-extrabold leading-[1.05] tracking-[-0.03em]"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span
              className="block"
              initial={{ y: "115%" }}
              animate={{ y: "0%" }}
              transition={{ delay: 0.4, duration: 0.9, ease: EASE }}
            >
              You're in{firstName ? `, ${firstName}` : ""}!
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.7, ease: EASE }}
          className="mt-4 max-w-[440px] text-[15px] font-medium leading-[1.62] text-[#141414] [text-wrap:balance] sm:text-[16.5px]"
        >
          We've received your details. Our team will reach out on WhatsApp
          shortly to schedule your personal call.
        </motion.p>

        <div className="mt-10 flex w-full flex-col gap-3">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85 + i * 0.14, duration: 0.6, ease: EASE }}
              className="flex items-center gap-4 rounded-[18px] border border-neutral-100 bg-white px-5 py-4 text-left shadow-[0_10px_30px_-18px_rgba(0,0,0,0.25)]"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F25C05]/10">
                <s.icon className="h-5 w-5 text-[#F25C05]" />
              </div>
              <div>
                <div className="text-[14.5px] font-bold">{s.title}</div>
                <div className="mt-0.5 text-[12.5px] font-medium leading-snug text-neutral-500">{s.text}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
